/**
 * The intent channel (overlay-system-design §7): "show this overlay once the user gets there",
 * posted before a navigation or an auth reload and read by the surface itself.
 *
 * One ordered array under one sessionStorage key. Each consuming surface reads its own intents
 * (`useIntents`) and acks one only once it is on screen, so an intent survives every navigation,
 * redirect, unmount and reload between the post and the moment the user actually sees it. That
 * is the whole contract: there is no router matching and no central consumer.
 *
 * sessionStorage because an intent belongs to this tab's sign-in: it must outlive a full-page
 * auth reload, and must not leak to another tab or across a sign-out (storageCleanup.ts sweeps
 * the key). It survives `AuthRouteOverlayReset`: intents are posted after the auth routes are
 * behind the user.
 *
 * Reads are pure (the snapshot is cached by the raw stored string), so render may read. Every
 * write — post, ack, the expiry prune — notifies subscribers synchronously.
 */
import { useMemo, useSyncExternalStore } from 'react';
import type { CelebrationKind } from '@utils/celebrationMarker';
import {
  isRegistrationCelebration,
  type RegistrationCelebration,
} from '@utils/registrationCelebration';
import { isEventPinCelebration, type EventPinCelebration } from '@utils/eventPinCelebration';
import { logger } from '@utils/logger';

export const INTENTS_STORAGE_KEY = 'mb:overlay-intents:v1';

/**
 * How long an unacknowledged intent stays eligible. Long enough to wait out a celebration and a
 * slow landing; short enough that an abandoned one can't pop a drawer on an unrelated later visit.
 */
export const INTENT_TTL_MS = 10 * 60 * 1000;

export interface IntentPayloads {
  celebration:
    | {
        kind: Exclude<CelebrationKind, 'registration_check' | 'event_pin'>;
        source: string | null;
      }
    // The ticketed Going celebration carries what its drawer needs; no payload, no drawer.
    | { kind: 'registration_check'; source: string | null; registration: RegistrationCelebration }
    // The PIN sheet carries the spaces it asks about; no payload, no sheet.
    | { kind: 'event_pin'; source: string | null; eventPin: EventPinCelebration };
  /** `community` is the slug or id the producer navigated with. */
  'community-join': { community: string };
  'community-welcome': { community: string };
  /**
   * `community` is the Space slug or id the Space screen should run the join for; absent means the
   * Events feed (TECH-847).
   */
  'create-event': { community?: string };
  /**
   * A poll answer cast after sign-in that needs its Space's PIN (TECH-197 E16): the poll's card
   * opens the PIN drawer still holding the pick. `spaceId` is the Space the server named.
   */
  'poll-pin': { questionId: string; optionIds: string[]; spaceId: string | null };
  /**
   * A live poll's join after sign-in that needs its Space's PIN (TECH-867 D7b): the live sheet opens today's PIN
   * drawer (`PinAccessDrawer`), whose Join sends `/live/{code}/join` with the PIN.
   */
  'live-pin': { liveCode: string };
}

export type IntentSurface = keyof IntentPayloads;

export type IntentDraft = {
  [S in IntentSurface]: { surface: S; payload: IntentPayloads[S] };
}[IntentSurface];

export type Intent = {
  [S in IntentSurface]: { id: string; surface: S; payload: IntentPayloads[S]; expiresAt: number };
}[IntentSurface];

/** One surface's intents. */
export type IntentFor<S extends IntentSurface> = Extract<Intent, { surface: S }>;

/** Deterministic per surface and key, so a re-post replaces rather than duplicates. */
function intentId(draft: IntentDraft): string {
  switch (draft.surface) {
    case 'celebration':
      // One ticket question per event. Keyed by kind alone, a second ticketed Going replaced the
      // first's stored payload, and the first's ack (by intent id, when it showed) then deleted
      // the second question unasked. The grant id stays per kind (CelebrationHost), so the second
      // still waits its turn and is claimed when the first closes.
      switch (draft.payload.kind) {
        case 'registration_check':
          return `celebration:registration_check:${draft.payload.registration.event_id}`;
        case 'event_pin':
          // One PIN sheet per event, for the same reason.
          return `celebration:event_pin:${draft.payload.eventPin.event_id}`;
        default:
          return `celebration:${draft.payload.kind}`;
      }
    case 'community-join':
    case 'community-welcome':
      return `${draft.surface}:${draft.payload.community}`;
    case 'create-event':
      return 'create-event';
    case 'poll-pin':
      return `poll-pin:${draft.payload.questionId}`;
    case 'live-pin':
      return `live-pin:${draft.payload.liveCode}`;
  }
}

const EMPTY: readonly Intent[] = Object.freeze([]);
let cachedRaw: string | null = null;
let cachedIntents: readonly Intent[] = EMPTY;

/** The only kinds a stored celebration intent may carry; anything else is dropped at parse. */
const CELEBRATION_KINDS = {
  signup: true,
  event_going: true,
  registration_check: true,
  event_pin: true,
} satisfies Record<CelebrationKind, true>;

function isValidPayload(surface: string, payload: unknown): boolean {
  if (typeof payload !== 'object' || payload === null) return false;
  const p = payload as Record<string, unknown>;
  const known = surface as IntentSurface;
  switch (known) {
    case 'celebration':
      return (
        typeof p.kind === 'string' &&
        Object.hasOwn(CELEBRATION_KINDS, p.kind) &&
        (p.source === null || typeof p.source === 'string') &&
        // `registration` rides on `registration_check` only, and that kind is useless without it:
        // a malformed or missing one drops the whole intent instead of rendering a broken drawer.
        (p.kind === 'registration_check'
          ? isRegistrationCelebration(p.registration)
          : p.registration === undefined) &&
        // `eventPin` rides on `event_pin` only, and that kind is useless without it.
        (p.kind === 'event_pin' ? isEventPinCelebration(p.eventPin) : p.eventPin === undefined)
      );
    case 'community-join':
    case 'community-welcome':
      return typeof p.community === 'string';
    case 'create-event':
      return p.community === undefined || typeof p.community === 'string';
    case 'poll-pin':
      return (
        typeof p.questionId === 'string' &&
        Array.isArray(p.optionIds) &&
        p.optionIds.length > 0 &&
        p.optionIds.every(id => typeof id === 'string') &&
        (p.spaceId === null || typeof p.spaceId === 'string')
      );
    case 'live-pin':
      return typeof p.liveCode === 'string' && p.liveCode.length > 0;
    default: {
      // Exhaustiveness guard (same idiom as MaintenanceTakeover): a new IntentSurface with no
      // case above is a compile error here, so a stored intent for it can never silently drop
      // on reload the way a plain `default: return false` would let it.
      const _exhaustive: never = known;
      void _exhaustive;
      return false;
    }
  }
}

function isIntent(value: unknown): value is Intent {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === 'string' &&
    typeof v.surface === 'string' &&
    Number.isFinite(v.expiresAt) &&
    isValidPayload(v.surface, v.payload)
  );
}

function readRaw(): string | null {
  try {
    return sessionStorage.getItem(INTENTS_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** The stored intents, stale ones included; the snapshot is stable until the stored string changes. */
export function getIntents(): readonly Intent[] {
  const raw = readRaw();
  if (raw === cachedRaw) return cachedIntents;
  cachedRaw = raw;
  if (raw === null) {
    cachedIntents = EMPTY;
    return cachedIntents;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    cachedIntents = Object.freeze(Array.isArray(parsed) ? parsed.filter(isIntent) : []);
  } catch {
    cachedIntents = EMPTY;
  }
  return cachedIntents;
}

export function isIntentFresh(intent: Intent, now: number): boolean {
  return now < intent.expiresAt;
}

const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach(listener => {
    listener();
  });
}

export function subscribeIntents(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

let expiryTimer: number | undefined;

const MAX_TIMEOUT_MS = 2 ** 31 - 1;

/** Re-arms the single prune timer for the earliest expiry, or clears it when nothing is stored. */
function scheduleExpiry(intents: readonly Intent[]): void {
  clearTimeout(expiryTimer);
  expiryTimer = undefined;
  if (intents.length === 0) return;
  const earliest = Math.min(...intents.map(i => i.expiresAt));
  // Clamped: setTimeout's delay is a signed 32-bit int, and a far-future expiresAt would
  // otherwise overflow into an immediate fire and a busy 1ms re-arm loop.
  const delay = Math.min(Math.max(0, earliest - Date.now()), MAX_TIMEOUT_MS);
  expiryTimer = window.setTimeout(() => {
    expiryTimer = undefined;
    pruneStale();
  }, delay);
}

/**
 * Writes the next array as the canonical in-memory state first, then tries to persist it. A
 * sessionStorage failure (quota, private-mode) is caught and logged, but the in-memory copy — and
 * everything downstream of `getIntents()` — still moves to the new state; only the doomed reload
 * loses it, not the rest of this tab's session. A reload restores the last persisted array, so a
 * failed write also drops whatever older array storage still held: persisting nothing beats
 * resurrecting stale, possibly-already-acked entries.
 */
function write(intents: readonly Intent[]): void {
  const frozen = Object.freeze(intents);
  try {
    if (frozen.length === 0) {
      sessionStorage.removeItem(INTENTS_STORAGE_KEY);
      cachedRaw = null;
    } else {
      const raw = JSON.stringify(frozen);
      sessionStorage.setItem(INTENTS_STORAGE_KEY, raw);
      cachedRaw = raw;
    }
  } catch (error) {
    // Storage may still hold an older array from before this failure; drop it so a reload
    // loses everything instead of resurrecting stale, possibly-already-acked entries.
    try {
      sessionStorage.removeItem(INTENTS_STORAGE_KEY);
    } catch {
      // Storage access itself is unavailable; nothing left to clean up.
    }
    cachedRaw = null;
    logger.warn(
      '[intents] sessionStorage write failed; the intent will not survive a reload',
      error,
    );
  }
  cachedIntents = frozen;
  scheduleExpiry(frozen);
  notify();
}

function withoutStale(intents: readonly Intent[], now: number): readonly Intent[] {
  const fresh = intents.filter(intent => isIntentFresh(intent, now));
  if (fresh.length !== intents.length) {
    logger.debug('[intents] dropped stale intents', {
      ids: intents.filter(intent => !fresh.includes(intent)).map(intent => intent.id),
    });
  }
  return fresh;
}

function pruneStale(): void {
  const current = getIntents();
  const fresh = withoutStale(current, Date.now());
  if (fresh.length !== current.length) write(fresh);
  else scheduleExpiry(current);
}

/**
 * Posts an intent. A re-post with the same id keeps its place in line and refreshes its payload
 * and expiry; distinct ids queue in post order.
 */
export function postIntent(draft: IntentDraft): string {
  const id = intentId(draft);
  const now = Date.now();
  const intent = { id, ...draft, expiresAt: now + INTENT_TTL_MS } as Intent;
  const current = withoutStale(getIntents(), now);
  const at = current.findIndex(existing => existing.id === id);
  write(
    at === -1 ? [...current, intent] : current.map((existing, i) => (i === at ? intent : existing)),
  );
  return id;
}

/** Removes an intent: its surface is on screen, or the intent no longer applies. */
export function ackIntent(id: string): void {
  const now = Date.now();
  const original = getIntents();
  const fresh = withoutStale(original, now);
  const next = fresh.filter(intent => intent.id !== id);
  if (next.length === original.length) return;
  write(next);
}

/** Empties the channel through the store: storage, the in-memory copy and subscribers. */
export function clearIntents(): void {
  write([]);
}

/** The pending intents for one surface, in post order. */
export function useIntents<S extends IntentSurface>(surface: S): readonly IntentFor<S>[] {
  const all = useSyncExternalStore(subscribeIntents, getIntents);
  return useMemo(
    () => all.filter((intent): intent is IntentFor<S> => intent.surface === surface),
    [all, surface],
  );
}

/** Clears the channel and its timer between tests. */
export function resetIntentsForTests(): void {
  clearTimeout(expiryTimer);
  expiryTimer = undefined;
  sessionStorage.removeItem(INTENTS_STORAGE_KEY);
  cachedRaw = null;
  cachedIntents = EMPTY;
}

// A reload keeps the stored intents; drop the ones that went stale while the tab was away.
pruneStale();
